/**
 * Two Sense — shared motion vocabulary.
 *
 * Ported from the approach in labeau_landing (Reveal.js / perf.js): scroll
 * reveals driven by one IntersectionObserver per element and animated with CSS
 * transitions, so the work happens on the compositor and no JavaScript runs per
 * frame. labeau_landing's `Reveal` depends on `react-intersection-observer`;
 * this project doesn't have that package, and the native API is all it used, so
 * this implementation drops the dependency.
 *
 * NOT ported from labeau_landing:
 *   · `src/components/animation.js` — a WOW.js compatibility layer that mutates
 *     `element.style` imperatively and runs a MutationObserver over the entire
 *     <body>. It fights React's rendering and costs a document-wide observer for
 *     effects `Reveal` gets for free.
 *   · AuroraCanvas / WaveField / Solutions3D — WebGL backdrops. They belong to a
 *     tech brand; the Two Sense guidelines call for hairlines and restraint
 *     ("nothing bounces, spins, or flashes").
 *
 * Reduced motion is handled globally in src/index.css, which collapses every
 * animation and transition to 0.01ms with `!important` (needed to beat MUI's
 * injected `sx` styles). Note that src/App.css contains a similar-looking rule
 * that has never done anything — nothing imports App.css.
 */

import { useEffect, useRef, useState } from 'react';

/* The single easing curve used across the site — matches
   `primitive.motion.ease` in assets/design-tokens.json. Everything decelerates
   the same way; that consistency is what makes the motion read as one system. */
export const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';

/* Durations, in seconds. Mirrors primitive.motion.duration-* in the tokens. */
export const DUR = {
    color: 0.35,
    lift: 0.55,
    entrance: 0.9,
    page: 0.55,
};

/**
 * Canonical viewport-entry hook.
 *
 * There are currently EIGHT hand-rolled copies of this across the codebase
 * (About, Products, Services, ImageCarousel, Appointment, OurStory, Contact,
 * ServicesPage) which have already drifted into five different
 * implementations. New code should import this one.
 *
 * @param {number}  threshold  fraction of the element that must be visible
 * @param {boolean} once       stop observing after the first entry
 * @returns {[React.RefObject, boolean]}
 */
export const useInView = (threshold = 0.15, once = true, rootMargin = '0px 0px -40px 0px') => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;

        // No IntersectionObserver (very old browsers, some test envs): show the
        // content rather than leaving it invisible forever.
        if (typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (once) observer.disconnect();
                } else if (!once) {
                    setInView(false);
                }
            },
            { threshold, rootMargin },
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [threshold, once, rootMargin]);

    return [ref, inView];
};

/**
 * A slow light sweep across a dark surface, for primary CTAs.
 *
 * labeau_landing's `shineSweep` animates `background-position` on a gradient
 * that is part of the element's own background. Here the sweep lives on a
 * `::after` pseudo-element instead, so it can be layered over the button's
 * existing ink gradient without replacing it.
 *
 * Spread into an `sx` prop:  sx={{ ...shineSweep(), ...rest }}
 */
export const shineSweep = ({ duration = 4.5, delay = 1.5 } = {}) => ({
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: '-60%',
        width: '40%',
        background:
            'linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)',
        transform: 'skewX(-18deg)',
        pointerEvents: 'none',
        animation: `ts-shine ${duration}s ${EASE} ${delay}s infinite`,
    },
});
